import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Router, NavigationEnd, ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import { AlertService } from '../services/alert.service';
import { Store } from '@ngrx/store';
import { NavigationService } from '../services/navigation.service';
import { AuthActions } from 'src/app/state/store/auth/auth.actions';

const filterOutErrorCodes = new Set(['ACCOUNT_LOCKED_CONTACT_SUPPORT', 'ACCOUNT_LOCKED_FOR_30_MINUTES', 'ACCOUNT_LOCKED_UNTIL_30_MINUTES', 'ACCOUNT_LOCKED_WITH_OTP_CONTACT_SUPPORT']);

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private networkErrorShown = false; // 👈 flag for per-route alerts

  constructor(
    private readonly router: Router,
    private readonly alertService: AlertService,
    private readonly store: Store,
    private readonly navigationService: NavigationService,
    private readonly activatedRoute: ActivatedRoute
  ) {
    // 👇 reset the flag whenever route changes
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.networkErrorShown = false;
      }
    });
  }

  intercept<T>(
    req: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<T>> {
    return next.handle(req).pipe(
      tap((event: HttpEvent<T>) => {
        if (event instanceof HttpResponse) {
          // if a success response comes, reset offline flag
          if (this.networkErrorShown) {
            this.networkErrorShown = false;
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // capture the request url that caused this interceptor to run
        const requestUrl = req.urlWithParams || req.url || error.url || undefined;

        if (error.error instanceof Blob && error.error.type === 'application/json') {
          return from(this.blobToJson(error.error)).pipe(
            switchMap((parsed: any) => {
              (error as any).error = parsed;
              return this.processHttpError(error, requestUrl);
            })
          );
        }
        return this.processHttpError(error, requestUrl);
      })
    );
  }

  private processHttpError(error: HttpErrorResponse, requestUrl?: string): Observable<never> {
    // Prefer the URL of the HTTP request that caused the error. Fall back to error.url, the browser address bar (window.location), or router.url.
    const browserUrl = globalThis.window?.location
      ? globalThis.window.location.pathname + (globalThis.window.location.search || '')
      : undefined;

    if( filterOutErrorCodes.has(error.error?.message) ) {
      // do not show anything when these errors occur
      // console.log('Filtered out error', error);
      return throwError(() => error);
    }

    // If this is the session-check request that runs during app initialization,
    // avoid performing a redirect/logout here to prevent redirect loops.
    const isSessionCheckRequest = !!requestUrl && requestUrl.includes('isSessionExist');

     switch (error.status) {
       case 401: {
         // allow the login page and any subroutes under /login (e.g. /login/verify, /login/reset)
         const authURLs = ['/login', '/account-locked', '/account-locked-temp', '/forgot-password', '/reset-password', '/set-password'];

         const isAuthRoute = !!browserUrl && authURLs.some(url => browserUrl.startsWith(url));
         // If this 401 comes from the session existence check, skip redirect/logout here
         if (isSessionCheckRequest) {
           // Let the caller/effect handle the session-not-exist flow to avoid loops
           console.log('401 from session check request - skipping interceptor redirect/logout');
           break;
         }

         if (!isAuthRoute) {
           this.alertService.info('Unauthorized access. Please log in again.');
           this.store.dispatch(AuthActions.logout({}));
           this.router.navigate(['/login']);
         }
         break;
       }
       case 403:
         this.alertService.error(
           error.error?.message ??
             'Forbidden access. You do not have permission.'
         );
         break;
       case 404:
         console.log('404 error', error);
         this.alertService.error(
           error.error?.message ?? 'Resource not found.'
         );
         break;
       case 500:
         this.alertService.error(
           error.error?.message ??
             'Internal server error. Please try again later.'
         );
         break;
       case 0:
         if (!this.networkErrorShown) {
           this.alertService.error(
             'Network error. Please check your internet connection.'
           );
           this.networkErrorShown = true;
         }
         break;
       default:
         this.alertService.error(
           error.error?.message || 'An unexpected error occurred.'
         );
     }

     return throwError(() => error);
   }

  // Build a readable route path from an ActivatedRouteSnapshot
  private getFullRoutePath(snapshot: ActivatedRouteSnapshot): string {
    const segments: string[] = [];
    let current: ActivatedRouteSnapshot | null = snapshot.root;
    while (current) {
      if (current.routeConfig?.path) {
        segments.push(current.routeConfig.path);
      }
      current = current.firstChild;
    }
    return segments.length ? '/' + segments.join('/') : this.router.url || '/';
  }


  private async blobToJson(blob: Blob): Promise<any> {
    try {
      const text = await blob.text();
      return JSON.parse(text);
    } catch (err) {
      console.error('Failed to read blob:', err);
      throw new Error('Failed to read blob: ' + (err instanceof Error ? err.message : String(err)));
    }
  }
}
